import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';

import { integrationsApi } from '../api/integrations';

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return value;
  }
};

const getErrorMessage = (err, fallback) => err?.response?.data?.detail || err?.message || fallback;

const IntegrationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);
  const [form, setForm] = useState({ name: '', expires_at: '' });

  const activeCount = useMemo(
    () => items.filter((item) => item.is_active && !item.revoked_at).length,
    [items]
  );

  const loadTokens = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await integrationsApi.listTokens();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось загрузить ключи'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const resetForm = () => {
    setForm({ name: '', expires_at: '' });
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      setError('Укажите название ключа');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const created = await integrationsApi.createToken({
        name: form.name.trim(),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      });
      setCreatedToken(created);
      setDialogOpen(false);
      resetForm();
      await loadTokens();
      setSuccess('Ключ создан. Сохраните его сейчас: повторно он не показывается.');
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось создать ключ'));
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (tokenId) => {
    setError('');
    setSuccess('');
    try {
      await integrationsApi.revokeToken(tokenId);
      await loadTokens();
      setSuccess('Ключ отключён');
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось отключить ключ'));
    }
  };

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setSuccess('Ключ скопирован');
    } catch {
      setError('Не удалось скопировать ключ');
    }
  };

  return (
    <Box
      className="home-container page-animate"
      sx={{
        px: { xs: 2, md: 3 },
        py: 3,
        maxWidth: 1280,
        mx: 'auto',
        width: '100%',
      }}
    >
      <Stack spacing={2.25}>
        <Paper
          sx={{
            p: { xs: 2.25, md: 2.75 },
            borderRadius: 3,
            border: '1px solid color-mix(in oklab, var(--border-color) 82%, transparent)',
            backgroundColor: 'var(--surface-bg)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="center">
                <VpnKeyRoundedIcon color="primary" />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  API-интеграции
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Ключи доступа для внешних систем.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Активных ключей: {activeCount}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => {
                  setCreatedToken(null);
                  setDialogOpen(true);
                }}
              >
                Создать ключ
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        {createdToken && (
          <Paper
            sx={{
              p: 2.25,
              borderRadius: 3,
              border: '1px solid rgba(22, 163, 74, 0.22)',
              backgroundColor: 'rgba(22, 163, 74, 0.05)',
            }}
          >
            <Stack spacing={1.25}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Новый ключ
              </Typography>
              <TextField fullWidth value={createdToken.plaintext_token} InputProps={{ readOnly: true }} />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyRoundedIcon />}
                  onClick={() => handleCopy(createdToken.plaintext_token)}
                >
                  Скопировать
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        <Paper
          sx={{
            p: 0,
            borderRadius: 3,
            border: '1px solid color-mix(in oklab, var(--border-color) 82%, transparent)',
            backgroundColor: 'var(--surface-bg)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
              <Box component="thead">
                <Box component="tr" sx={{ backgroundColor: 'color-mix(in oklab, var(--surface-muted) 76%, transparent)' }}>
                  {['Название', 'Префикс', 'Создан', 'Последнее использование', 'Истекает', 'Статус', 'Действия'].map((title) => (
                    <Box
                      key={title}
                      component="th"
                      sx={{
                        px: 2,
                        py: 1.5,
                        textAlign: 'left',
                        fontSize: 13,
                        fontWeight: 700,
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {title}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {!loading && items.length === 0 && (
                  <Box component="tr">
                    <Box component="td" colSpan={7} sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.secondary' }}>
                      Ключей пока нет
                    </Box>
                  </Box>
                )}
                {items.map((item) => {
                  const isExpired = item.expires_at && new Date(item.expires_at) < new Date();
                  const isRevoked = !item.is_active || Boolean(item.revoked_at);
                  const statusLabel = isRevoked ? 'Отключён' : isExpired ? 'Истёк' : 'Активен';
                  return (
                    <Box
                      component="tr"
                      key={item.id}
                      sx={{
                        borderTop: '1px solid color-mix(in oklab, var(--border-color) 72%, transparent)',
                        '&:hover': {
                          backgroundColor: 'color-mix(in oklab, var(--surface-muted) 58%, transparent)',
                        },
                      }}
                    >
                      <Box component="td" sx={{ px: 2, py: 1.75, fontWeight: 600 }}>{item.name}</Box>
                      <Box component="td" sx={{ px: 2, py: 1.75, fontFamily: 'monospace' }}>{item.token_prefix}…</Box>
                      <Box component="td" sx={{ px: 2, py: 1.75 }}>{formatDateTime(item.created_at)}</Box>
                      <Box component="td" sx={{ px: 2, py: 1.75 }}>{formatDateTime(item.last_used_at)}</Box>
                      <Box component="td" sx={{ px: 2, py: 1.75 }}>{formatDateTime(item.expires_at)}</Box>
                      <Box component="td" sx={{ px: 2, py: 1.75 }}>
                        <Chip
                          size="small"
                          label={statusLabel}
                          color={isRevoked ? 'error' : isExpired ? 'warning' : 'success'}
                          variant="outlined"
                        />
                      </Box>
                      <Box component="td" sx={{ px: 2, py: 1.75 }}>
                        {!isRevoked && (
                          <Button
                            size="small"
                            color="error"
                            variant="text"
                            startIcon={<DeleteOutlineRoundedIcon />}
                            onClick={() => handleRevoke(item.id)}
                          >
                            Отключить
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (!saving) setDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Создать API-ключ</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Stack spacing={2}>
            <TextField
              label="Название"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label="Дата истечения"
              type="datetime-local"
              value={form.expires_at}
              onChange={(event) => setForm((prev) => ({ ...prev, expires_at: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Отмена
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            Создать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntegrationsPage;
